import { useEffect } from "react";
import { publicMediaSrc, toConfiguredAbsoluteUrl } from "../config/api";
import { productHref } from "./CategoryCard";
import { deliveryHref } from "./deliveryHref";
import deliveriesShareImage from "../assets/deliveries-hero.jpg";

const DEFAULT_DOCUMENT_TITLE = "Steel, Hardware & Industrial Supplies Malaysia";
const DEFAULT_DOCUMENT_DESCRIPTION =
  "Steel, hardware, fasteners, roofing, paint and industrial products for contractors across Malaysia. Request a quote today.";
const PAGE_JSON_LD_ID = "delivery-webpage-jsonld";
const BREADCRUMB_JSON_LD_ID = "delivery-breadcrumb-jsonld";
const TITLE_MAX = 60;
const DESCRIPTION_MAX = 160;

function cleanText(value) {
  const text = String(value ?? "").trim();
  if (!text || text === "undefined" || text === "null") return "";
  return text;
}

function oneLine(value) {
  return cleanText(value).replace(/\s+/g, " ").trim();
}

function stripHtml(value) {
  return oneLine(String(value ?? "").replace(/<[^>]*>/g, " "));
}

function fitLength(text, max) {
  const clean = oneLine(text);
  if (!clean || clean.length <= max) return clean;
  const slice = clean.slice(0, max);
  const breakAt = slice.lastIndexOf(" ");
  const cut = breakAt > 0 ? slice.slice(0, breakAt) : slice;
  return cut.replace(/[\s,.;:!?-]+$/g, "");
}

function containsSiteName(title, siteName) {
  if (!title || !siteName) return false;
  return title.toLowerCase().includes(siteName.toLowerCase());
}

function upsertMeta(attr, key, content) {
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector(selector);
  if (!content) {
    if (el && el.dataset.deliverySeo === "true") el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  el.dataset.deliverySeo = "true";
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!href) {
    if (el && el.dataset.deliverySeo === "true") el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
  el.dataset.deliverySeo = "true";
}

function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function prune(value) {
  if (Array.isArray(value)) {
    const next = value.map(prune).filter((item) => item !== undefined);
    return next.length ? next : undefined;
  }
  if (value && typeof value === "object") {
    const next = {};
    Object.keys(value).forEach((key) => {
      const item = prune(value[key]);
      if (item !== undefined && item !== "") next[key] = item;
    });
    return Object.keys(next).length ? next : undefined;
  }
  if (value === undefined || value === null || value === "") return undefined;
  return value;
}

function clearDeliverySeo() {
  document.head.querySelectorAll("[data-delivery-seo='true']").forEach((el) => el.remove());
  upsertJsonLd(PAGE_JSON_LD_ID, null);
  upsertJsonLd(BREADCRUMB_JSON_LD_ID, null);
}

function idOf(value) {
  if (!value) return "";
  if (typeof value === "object") return String(value._id || value.id || "");
  return String(value).trim();
}

function productUrlOf(row, products) {
  const slugPath = cleanText(row?.categorySlug) && cleanText(row?.productSlug)
    ? `/category/${cleanText(row.categorySlug)}/${cleanText(row.productSlug)}`
    : "";
  if (slugPath && row?.productPublished !== false) return slugPath;
  const catalog = (Array.isArray(products) ? products : []).find((item) => idOf(item) === idOf(row?.product));
  const href = catalog ? productHref(catalog) : "";
  return href && href !== "/products" ? href : "";
}

export function deliveryPageTitle(delivery, contact) {
  const siteName = cleanText(contact?.siteName);
  const seoTitle = cleanText(delivery?.seoTitle);
  if (seoTitle) return fitLength(seoTitle, TITLE_MAX);
  const title = cleanText(delivery?.title);
  if (title && siteName && !containsSiteName(title, siteName)) {
    return fitLength(`${title} | ${siteName}`, TITLE_MAX);
  }
  if (title) return fitLength(title, TITLE_MAX);
  if (siteName) return fitLength(siteName, TITLE_MAX);
  return DEFAULT_DOCUMENT_TITLE;
}

export function deliveryPageDescription(delivery, contact) {
  const seoDescription = stripHtml(delivery?.seoDescription);
  if (seoDescription) return fitLength(seoDescription, DESCRIPTION_MAX);
  const description = stripHtml(delivery?.description);
  if (description) return fitLength(description, DESCRIPTION_MAX);
  const names = (Array.isArray(delivery?.product) ? delivery.product : [])
    .map((item) => cleanText(item?.productName))
    .filter(Boolean)
    .join(", ");
  const location = cleanText(delivery?.deliveryLocation);
  const siteName = cleanText(contact?.siteName);
  if (names && location && siteName) {
    return fitLength(`${names} delivered to ${location}. View this delivery by ${siteName}.`, DESCRIPTION_MAX);
  }
  if (names && location) {
    return fitLength(`${names} delivered to ${location}.`, DESCRIPTION_MAX);
  }
  if (location && siteName) {
    return fitLength(`Delivery to ${location} by ${siteName}.`, DESCRIPTION_MAX);
  }
  return DEFAULT_DOCUMENT_DESCRIPTION;
}

function applyBlockedMetadata(title) {
  document.title = title;
  upsertMeta("name", "description", DEFAULT_DOCUMENT_DESCRIPTION);
  upsertLink("canonical", "");
  upsertMeta("name", "robots", "noindex,nofollow");
  upsertMeta("property", "og:type", "");
  upsertMeta("property", "og:title", "");
  upsertMeta("property", "og:description", "");
  upsertMeta("property", "og:url", "");
  upsertMeta("property", "og:site_name", "");
  upsertMeta("property", "og:image", "");
  upsertMeta("property", "og:image:alt", "");
  upsertMeta("property", "og:locale", "");
  upsertMeta("name", "twitter:card", "");
  upsertMeta("name", "twitter:title", "");
  upsertMeta("name", "twitter:description", "");
  upsertMeta("name", "twitter:image", "");
  upsertMeta("name", "twitter:image:alt", "");
  upsertJsonLd(PAGE_JSON_LD_ID, null);
  upsertJsonLd(BREADCRUMB_JSON_LD_ID, null);
}

function DeliverySeo({ contact, contactStatus = "loading", delivery = null, products = [], status = "loading" }) {
  useEffect(() => {
    const siteName = cleanText(contact?.siteName);

    if (status === "loading" || contactStatus === "loading") {
      applyBlockedMetadata(siteName ? `Loading delivery | ${siteName}` : "Loading delivery");
      return () => {
        document.title = DEFAULT_DOCUMENT_TITLE;
        clearDeliverySeo();
      };
    }

    if (status === "error" || status === "missing" || !delivery?.slug || delivery.isPublished === false) {
      applyBlockedMetadata(
        status === "error"
          ? siteName
            ? `Unable to load delivery | ${siteName}`
            : "Unable to load delivery"
          : "Page not found"
      );
      return () => {
        document.title = DEFAULT_DOCUMENT_TITLE;
        clearDeliverySeo();
      };
    }

    const path = deliveryHref(delivery);
    const canonicalUrl = path && path !== "/deliveries" ? toConfiguredAbsoluteUrl(path) : "";
    const homeUrl = toConfiguredAbsoluteUrl("/");
    const listUrl = toConfiguredAbsoluteUrl("/deliveries");
    const websiteUrl = String(homeUrl || "/").replace(/\/$/, "");
    const pageTitle = deliveryPageTitle(delivery, contact);
    const pageDescription = deliveryPageDescription(delivery, contact);
    const firstImage = Array.isArray(delivery.images) ? delivery.images.find((item) => publicMediaSrc(item?.url)) : null;
    const imagePath = firstImage ? publicMediaSrc(firstImage.url) : "";
    const imageUrl = imagePath
      ? toConfiguredAbsoluteUrl(imagePath)
      : toConfiguredAbsoluteUrl(deliveriesShareImage);
    const imageAlt =
      cleanText(firstImage?.altText) ||
      (cleanText(delivery.title) && cleanText(delivery.deliveryLocation)
        ? `${cleanText(delivery.title)} delivery at ${cleanText(delivery.deliveryLocation)}`
        : cleanText(delivery.title));
    const robots =
      status === "redirect" ? "noindex,nofollow" : "index,follow,max-image-preview:large";

    document.title = pageTitle;
    upsertMeta("name", "description", pageDescription);
    upsertLink("canonical", canonicalUrl);
    upsertMeta("name", "robots", robots);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:title", pageTitle);
    upsertMeta("property", "og:description", pageDescription);
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:site_name", siteName);
    upsertMeta("property", "og:image", imageUrl);
    upsertMeta("property", "og:image:alt", imageUrl ? imageAlt : "");
    upsertMeta("property", "og:locale", "en_MY");
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", pageTitle);
    upsertMeta("name", "twitter:description", pageDescription);
    upsertMeta("name", "twitter:image", imageUrl);
    upsertMeta("name", "twitter:image:alt", imageUrl ? imageAlt : "");

    const about = (Array.isArray(delivery.product) ? delivery.product : [])
      .map((row) => {
        const name = cleanText(row?.productName);
        if (!name) return null;
        const href = productUrlOf(row, products);
        return {
          "@type": "Product",
          name,
          url: href ? toConfiguredAbsoluteUrl(href) : undefined,
        };
      })
      .filter(Boolean);

    upsertJsonLd(
      PAGE_JSON_LD_ID,
      prune({
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": canonicalUrl ? `${canonicalUrl}#webpage` : undefined,
        url: canonicalUrl || undefined,
        name: pageTitle,
        description: pageDescription,
        datePublished: delivery.createdAt || undefined,
        dateModified: delivery.updatedAt || undefined,
        inLanguage: "en-MY",
        isPartOf: siteName
          ? {
              "@type": "WebSite",
              "@id": `${websiteUrl || ""}/#website`,
              url: websiteUrl || homeUrl || undefined,
              name: siteName,
            }
          : undefined,
        primaryImageOfPage: imageUrl
          ? { "@type": "ImageObject", url: imageUrl, caption: imageAlt || undefined }
          : undefined,
        about: about.length ? about : undefined,
        contentLocation: cleanText(delivery.deliveryLocation)
          ? { "@type": "Place", name: cleanText(delivery.deliveryLocation) }
          : undefined,
      })
    );

    upsertJsonLd(
      BREADCRUMB_JSON_LD_ID,
      prune({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "@id": canonicalUrl ? `${canonicalUrl}#breadcrumb` : undefined,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: homeUrl },
          { "@type": "ListItem", position: 2, name: "Deliveries", item: listUrl },
          {
            "@type": "ListItem",
            position: 3,
            name: cleanText(delivery.title) || pageTitle,
            item: canonicalUrl,
          },
        ],
      })
    );

    return () => {
      document.title = DEFAULT_DOCUMENT_TITLE;
      clearDeliverySeo();
    };
  }, [contact, contactStatus, delivery, products, status]);

  return null;
}

export default DeliverySeo;
